import React, { useState, useEffect, forwardRef, useRef } from "react";
import { useAppContext } from "../../../context/AppContext";
import useLocalization from "../../../hooks/useLocalization";
import { motion, AnimatePresence } from "framer-motion";
import { Info } from "lucide-react";
import SocketMicStreamer from "../InputUI/SocketMicStreamerNewUi.jsx";
import { useLogger } from "../../../utils/UnifiedLogger.jsx";
import { useSelector, useDispatch } from "react-redux";

import { setIsSuggestedQuestionsVisible } from "../../../store/features/message/textContentSlice.js";
import {
  selectAccumulatedFinalTranscript,
  selectCurrentInterimTranscript,
  selectIsSocketRecording,
} from "../../../store/features/message/textContentSelectors.js";
import PropTypes from "prop-types";
import { selectIsBigMicButtonClicked } from "../../../store/features/ui/uiSelectors.js";

const SuggestedQuestions = forwardRef((props, ref) => {
  const { logInfo } = useLogger("SuggestedQuestions", true);
  const dispatch = useDispatch();
  const { userData, selectedLanguageData } = useAppContext();

  const accumulatedFinalTranscript = useSelector(
    selectAccumulatedFinalTranscript
  );
  const currentInterimTranscript = useSelector(selectCurrentInterimTranscript);
  const getIsSocketRecording = useSelector(selectIsSocketRecording);

  const {
    questions = [],
    onSuggestedQuestionClick = () => {},
    handleSend = () => {},
  } = props;

  const { t, setLanguage } = useLocalization();
  const micStreamerRef = useRef(null);
  const suggestedQuestionRef = useRef(null);

  const [index, setIndex] = useState(0);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const isBigMuttonClicked = useSelector(selectIsBigMicButtonClicked);

  const combinedTranscript = [
    accumulatedFinalTranscript,
    currentInterimTranscript,
  ]
    .filter(Boolean)
    .join(" ");

  // Auto-scroll effect - runs every 15 seconds
  useEffect(() => {
    if (!questions.length || getIsSocketRecording) return;

    const interval = setInterval(() => {
      setIndex((prevIndex) => (prevIndex + 1) % questions.length);
    }, 3000); // 15 seconds

    return () => clearInterval(interval);
  }, [questions.length, getIsSocketRecording]);

  useEffect(() => {
    if (!suggestedQuestionRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        dispatch(setIsSuggestedQuestionsVisible(entry.isIntersecting));
      },
      { threshold: 0.1 }
    );

    observer.observe(suggestedQuestionRef.current);

    return () => {
      if (suggestedQuestionRef.current) {
        observer.unobserve(suggestedQuestionRef.current);
      }
    };
  }, [suggestedQuestionRef]);

  useEffect(() => {
    if (selectedLanguageData?.code) {
      setLanguage(selectedLanguageData.code);
    }
  }, [selectedLanguageData?.code, setLanguage]);

  useEffect(() => {
    if (accumulatedFinalTranscript) {
      logInfo(
        "ACCUMULATED_FINAL_TRANSCRIPT_UPDATE",
        "Accumulated final transcript updated",
        { accumulatedFinalTranscript }
      );
    }
  }, [accumulatedFinalTranscript]);

  useEffect(() => {
    const hasSeenDisclaimer = localStorage.getItem("disclaimerShown");
    if (!hasSeenDisclaimer) {
      setDisclaimerOpen(false);
      localStorage.setItem("disclaimerShown", "true");
    }
  }, []);

  const portalUrl = import.meta.env.VITE_PORTAL_REDIRECTION_URL;

  return (
    <>
      {disclaimerOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-xl shadow-lg text-center max-w-lg pt-6 pb-3 w-full relative border-t-[5px] border-t-[#EAB308]">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Info className="w-10 h-10 rotate-180 text-white fill-yellow-500 flex-shrink-0 animate-bounce" />
            </div>
            <p className="text-md px-2 font-medium">
              {t("navbar.disclaimer")}
              <a href={portalUrl} className="text-blue-600">
                {portalUrl}
              </a>
            </p>
            <div className="mt-6 flex justify-center border-t border-t-[#ddd]">
              <button
                onClick={() => setDisclaimerOpen(false)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 mt-2"
              >
                {t("close") || "Close"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="relative sm:bg-gradient-to-r from-[#E17100] via-[#F0B001] to-[#E17100] text-black text-center sm:text-start sm:text-white p-2 mt-3 mb-5 sm:p-4 sm:m-5 rounded-md">
        <div className="absolute hidden sm:block inset-0 rounded-md wlcm-bg-gif"></div>
  <div className="relative items-center justify-center h-full">
    <p className="font-extrabold">
            <span className="text-[#F97316] sm:text-[#FFDDC5]">
        {t("greeting.hello") || "Hi"}
      </span>{" "}
      {userData.name?.split(" ").slice(-1)[0]}
    </p>
          <p className="text-sm">
      <span className="font-extrabold">
              {t("greeting.welcomeMessage") || "welcome"}{" "}
      </span>
      {t("greeting.toThe") || "to the"}{" "}
            <span className="text-[#F97316] sm:text-[#FFDDC5] font-extrabold">
              {t("greeting.cpgrams_assistance") || "CPGRAMS AI Assistance"}
      </span>{" "}
      {t("greeting.assist") ||
        "You can register your grievance via speaking or typing."}
    </p>
  </div>
</div>

      {/* CRITICAL FIX: Always render SocketMicStreamer - NEVER unmount it */}
      <div>
        <SocketMicStreamer
          key="mic-streamer-main"
          ref={micStreamerRef}
          isUserInputBar={false}
          onAutoSend={handleSend}
        />

        {/* Show "Tap to Speak" when NOT recording */}
        {!isBigMuttonClicked && (
          <div className="flex justify-center mt-7">
            <motion.p
              className="text-[19px] font-inter font-extrabold tracking-widest text-center 
      bg-gradient-to-b from-[#FC9D6D] via-[#FE6700] to-[#D66F29]
      bg-clip-text text-transparent bg-[length:200%_200%]"
              animate={{
                backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                scale: [1, 1.05, 1],
              }}
              transition={{
                backgroundPosition: {
                  duration: 3,
                  repeat: Infinity,
                  ease: "linear",
                },
                scale: { duration: 1.8, repeat: Infinity, ease: "easeInOut" },
              }}
            >
              {t("tapToSpeak")}
            </motion.p>
          </div>
        )}

        {/* Show suggested questions when NOT recording */}
        {!isBigMuttonClicked && (
          <div className="mx-2" ref={suggestedQuestionRef}>
            <div className="border-[2px] border-dashed border-[#FE6700] max-w-4xl rounded-md mx-auto text-center mt-9 p-2 sm:p-5 pb-2">
              <h5 className="font-semibold bg-[#FFF4E0] p-3 -mt-9 sm:-mt-11 w-fit mx-auto">
                {t("suggestedQuestions.heading") || "Example Grievances"}
              </h5>
              <AnimatePresence mode="wait">
                <motion.p
                  key={index}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="shadow-md cursor-pointer rounded-full p-2 border-[0.5px] border-[#FE6700] border-l-[6px] border-l-[#FE6700] bg-white hover:bg-[#FE6700] hover:text-white hover:border-l-[#FE6700] transition-colors"
                  onClick={() =>
                    onSuggestedQuestionClick(questions[index], index)
                  }
                >
                  {questions[index]}
                </motion.p>
              </AnimatePresence>
            </div>
            <div className="flex gap-2 mt-2 justify-center">
              {questions.map((question, i) => (
                <button
  key={question}
  onClick={() => setIndex(i)}
  title="Click"
  className="w-3 h-3 rounded-full"
  style={{
    backgroundColor: index === i ? "#FE6700" : "#a5abb6"
  }}
/>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
});

SuggestedQuestions.propTypes = {
  questions: PropTypes.arrayOf(PropTypes.string),
  onSuggestedQuestionClick: PropTypes.func,
};

SuggestedQuestions.displayName = "SuggestedQuestions";
export default SuggestedQuestions;
